import { useState } from 'react'
import { Plus, Search, Building2, User, ChevronLeft, ChevronRight } from 'lucide-react'
import { useClients } from '../api/clients'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../components/ui/Table'
import { formatDate } from '../lib/utils'
import ClientForm from '../components/clients/ClientForm'
import ClientDetailModal from '../components/clients/ClientDetailModal'
import type { ClientType } from '@1130-studio/shared'

const typeFilters: { label: string; value: ClientType | undefined; icon: React.ReactNode }[] = [
  { label: 'Все', value: undefined, icon: null },
  { label: 'Компании', value: 'company', icon: <Building2 className="w-4 h-4" /> },
  { label: 'Физлица', value: 'individual', icon: <User className="w-4 h-4" /> },
]

export default function ClientsPage() {
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<ClientType | undefined>()
  const [page, setPage] = useState(1)
  const [showForm, setShowForm] = useState(false)
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null)

  const { data, isLoading, error } = useClients({
    type: typeFilter,
    search: search || undefined,
    page,
    pageSize: 20,
  })

  const clients = data?.items || []
  const totalPages = data?.totalPages || 1

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Клиенты</h1>
          <p className="text-text-secondary mt-1">
            База клиентов студии
          </p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="w-4 h-4" />
          Новый клиент
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
          <Input
            placeholder="Поиск по имени, компании, email..."
            className="pl-10"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
          />
        </div>
        <div className="flex gap-2">
          {typeFilters.map((filter) => (
            <button
              key={filter.label}
              onClick={() => {
                setTypeFilter(filter.value)
                setPage(1)
              }}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                typeFilter === filter.value
                  ? 'bg-accent-lime-dim text-accent-lime'
                  : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
              }`}
            >
              {filter.icon}
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-accent-lime border-t-transparent rounded-full" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-400">
            Ошибка загрузки клиентов
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-12 text-text-tertiary">
            {search || typeFilter ? 'Клиенты не найдены' : 'Пока нет клиентов'}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Тип</TableHead>
                <TableHead>Название / Имя</TableHead>
                <TableHead>Контакты</TableHead>
                <TableHead>ИНН</TableHead>
                <TableHead>Создан</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((client) => (
                <TableRow
                  key={client.id}
                  className="cursor-pointer"
                  onClick={() => setSelectedClientId(client.id)}
                >
                  <TableCell>
                    {client.type === 'company' ? (
                      <Badge variant="info">
                        <Building2 className="w-3 h-3 mr-1" />
                        Компания
                      </Badge>
                    ) : (
                      <Badge variant="violet">
                        <User className="w-3 h-3 mr-1" />
                        Физлицо
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      {client.type === 'company' && client.companyName && (
                        <div className="font-medium">{client.companyName}</div>
                      )}
                      <div className={client.type === 'company' ? 'text-sm text-text-secondary' : 'font-medium'}>
                        {client.contactName}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1 text-sm">
                      {client.email && (
                        <div className="text-text-secondary">{client.email}</div>
                      )}
                      {client.phone && (
                        <div className="text-text-tertiary">{client.phone}</div>
                      )}
                      {client.telegram && (
                        <div className="text-text-tertiary">@{client.telegram.replace('@', '')}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {client.inn ? (
                      <span className="font-mono text-sm text-text-secondary">{client.inn}</span>
                    ) : (
                      <span className="text-text-tertiary">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-text-tertiary">
                      {formatDate(client.createdAt)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border-subtle">
            <div className="text-sm text-text-tertiary">
              Страница {page} из {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create Client Modal */}
      {showForm && (
        <ClientForm
          onClose={() => setShowForm(false)}
          onSuccess={() => setShowForm(false)}
        />
      )}

      {/* Client Detail Modal */}
      {selectedClientId && (
        <ClientDetailModal
          clientId={selectedClientId}
          isOpen={!!selectedClientId}
          onClose={() => setSelectedClientId(null)}
        />
      )}
    </div>
  )
}
